/**
 * useRobustReplaceableAction — generic safety wrapper for modifying any
 * user replaceable event (kind:3, kind:10002, kind:10317, etc.).
 *
 * WHY THIS EXISTS
 * ---------------
 * Replaceable events (kinds 0, 3, 10000-19999) have a critical property:
 * only the latest event per pubkey+kind is kept. If the EventStore holds a
 * stale copy and an action modifies it, the published event will silently
 * overwrite changes made on another client.
 *
 * This hook adds three layers of protection:
 *
 * 1. RELAY-GROUP QUORUM
 *    The user's NIP-65 outboxes are the voters. One third must return real
 *    EOSE, bounded to one through three. If no mailbox list is known, half of
 *    the configured fallback relays vote instead. Lookup relays can contribute
 *    a newer event but never veto the write.
 *
 * 2. FRESH EXACT-COORDINATE FETCH
 *    The write waits for actual EOSE from the required voters. Cached data and
 *    socket connectivity cannot satisfy the proof. Address and exact-event
 *    deletion requests are refreshed before the action reads the store.
 *
 * 3. PERSISTENT BACKGROUND SUBSCRIPTION (handled in accounts.ts)
 *    A continuous pool.subscription() for all user replaceable kinds is kept
 *    open on the union of the user's outbox relays and lookup/index relays
 *    for the lifetime of the session (see userIdentitySubscription.ts). This
 *    means the EventStore is already warm in most cases; the click-time quorum
 *    is still required because background data is not freshness evidence.
 *
 * USAGE
 * -----
 * ```ts
 * const execute = useRobustReplaceableAction();
 *
 * // Wrap any action that modifies a replaceable event:
 * await execute(3, async () => {
 *   await followUser(pubkey);
 * });
 * ```
 */

import { useCallback, useState } from "react";
import { useActiveAccount } from "applesauce-react/hooks";
import { useEventStore } from "@/hooks/useEventStore";
import { pool } from "@/services/nostr";
import { fallbackRelays, lookupRelays } from "@/services/settings";
import { requestPersonalSnapshotWithDiscovery } from "@/lib/personalReplaceableSnapshot";

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/**
 * How long (ms) to wait for the named voter quorum to return real EOSE.
 */
const FETCH_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface RobustReplaceableActionResult {
  /**
   * Execute an action that modifies a replaceable event, with connectivity
   * and freshness safeguards.
   *
   * @param kind    - The replaceable event kind being modified
   * @param action  - The async action to execute after safety checks pass
   */
  execute: (kind: number, action: () => Promise<void>) => Promise<void>;
  /** True while an action is in progress. */
  pending: boolean;
}

export function useRobustReplaceableAction(): RobustReplaceableActionResult {
  const account = useActiveAccount();
  const store = useEventStore();
  const [pending, setPending] = useState(false);

  const execute = useCallback(
    async (kind: number, action: () => Promise<void>) => {
      if (!account?.pubkey) {
        throw new Error("Not logged in.");
      }

      setPending(true);
      try {
        if (!navigator.onLine) {
          throw new Error(
            "You appear to be offline. Please check your internet connection and try again.",
          );
        }
        await requestPersonalSnapshotWithDiscovery({
          pool,
          store,
          ownerPubkey: account.pubkey,
          target: { pubkey: account.pubkey, kind },
          lookupRelays: lookupRelays.getValue(),
          fallbackRelays: fallbackRelays.getValue(),
          maxSettleTime: FETCH_TIMEOUT_MS,
        });

        // Run the action from the freshly updated EventStore projection.
        await action();
      } finally {
        setPending(false);
      }
    },
    [account?.pubkey, store],
  );

  return { execute, pending };
}
